/**
 * @fileoverview Template variable substitution utility
 * @module screen-view-v2/utils/templateParser
 *
 * Handles template variable substitution for paywall and dynamic content.
 *
 * Supported variables (FR-060):
 * - @priceAndCurrency - Full price with currency symbol
 * - @price - Price value only
 * - @duration - Subscription duration
 * - @interval - Billing interval (month/year/week)
 * - @trialDays - Trial period in days
 * - @fullTrialDays - Full trial period description
 * - @introPrice - Introductory price
 * - @introProduct - Introductory product name
 * - @introDuration - Introductory duration
 */

/**
 * Variables object type for template substitution
 */
export type TemplateVariables = Record<string, string>;

/**
 * Substitutes template variables in text using regex pattern.
 *
 * Uses @reference matching to find and replace variables (FR-061).
 * Variables not found in the variables object are left unchanged (FR-059).
 *
 * @param text - The text containing template variables (e.g., "Price: @price")
 * @param variables - Object mapping variable names to values (optional)
 * @returns Text with variables substituted
 *
 * @example
 * substituteVariables("Only @price per @interval!", { price: "$9.99", interval: "month" })
 * // Returns: "Only $9.99 per month!"
 *
 * @example
 * substituteVariables("@unknownVar here", {})
 * // Returns: "@unknownVar here" (unchanged)
 */
export function substituteVariables(
  text: string,
  variables?: TemplateVariables
): string {
  // Return text unchanged if no variables provided
  if (!variables || Object.keys(variables).length === 0) {
    return text;
  }

  // Supports @variable and @variable.key references. Hyphenated keys are kept
  // for backward compatibility with older UUID-based @mentions.
  return text.replace(/@([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)?)/g, (match, variableName) => {
    // Check if variable exists in the variables object
    if (variableName in variables) {
      return variables[variableName];
    }
    // Leave unchanged if variable not found
    return match;
  });
}
